/**
 * How much room a state placeholder may occupy.
 *
 * - `inline` — one muted row, no chrome. Popovers, table cells, list rows.
 * - `panel` — centered icon tile, title and description. Card bodies, tabs, sheet sections.
 * - `page` — the `panel` shape at route scale. Routes, MFE parcels.
 */
export type StatePlaceholderDensity = 'inline' | 'panel' | 'page';

/** Densities that render the icon inside a bordered tile. `inline` shows a bare glyph. */
export type TileDensity = Exclude<StatePlaceholderDensity, 'inline'>;

// No `w-full` here: it is a no-op for a block-level div in normal flow, but inside a flex
// parent it stops the placeholder shrinking to fit. Semantic components that want it opt in.
export const ROOT_CLASSES: Record<StatePlaceholderDensity, string> = {
  inline: 'flex items-start justify-start gap-2 py-2',
  panel:
    'flex flex-col items-center justify-center gap-3 px-4 py-8 text-center',
  page: 'flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center',
};

// Radius scales with the tile. A fixed `rounded-4xl` read as a squircle at 48px and a rounded
// square at 80px — one component rendering two different shapes.
export const TILE_CLASSES: Record<TileDensity, string> = {
  panel: 'size-12 rounded-2xl',
  page: 'size-20 rounded-4xl',
};

export const ICON_CLASSES: Record<StatePlaceholderDensity, string> = {
  inline: 'mt-0.5 size-4',
  panel: 'size-5',
  page: 'size-6',
};

export const TITLE_CLASSES: Record<StatePlaceholderDensity, string> = {
  inline: 'text-sm font-semibold leading-5',
  panel: 'text-base font-semibold leading-6',
  page: 'text-lg font-semibold leading-7',
};

/**
 * The element a title renders as when the caller does not name one.
 *
 * Route scale gets a real heading: a route with no heading leaves screen-reader users nothing
 * to navigate to. The smaller densities sit inside a section that already owns its heading, so
 * a second one there breaks the document outline.
 */
export const DEFAULT_TITLE_ELEMENT: Record<
  StatePlaceholderDensity,
  'h1' | 'p'
> = {
  inline: 'p',
  panel: 'p',
  page: 'h1',
};
